import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  orgApi,
  type ActivityEntry,
  type AuditEntry,
  type OrgAddress,
  type OrgContact,
  type Organization,
  type SettingsBundle,
} from '../../api/organizations';

type Tab = 'overview' | 'profile' | 'branding' | 'settings' | 'contacts' | 'addresses' | 'activity' | 'audit';

export function OrganizationDetailPage() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as Tab | null) ?? 'overview';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [org, setOrg] = useState<Organization | null>(null);
  const [settings, setSettings] = useState<SettingsBundle | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [contacts, setContacts] = useState<OrgContact[]>([]);
  const [addresses, setAddresses] = useState<OrgAddress[]>([]);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', role: '', is_primary: false });
  const [newAddress, setNewAddress] = useState({
    label: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    is_primary: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function loadOrg() {
    setLoading(true);
    setError('');
    try {
      setOrg(await orgApi.get(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadOrg();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (tab === 'settings') {
      void orgApi.getSettings(id).then(setSettings).catch(() => setSettings(null));
    }
    if (tab === 'activity') {
      void orgApi.getActivity(id).then(setActivity).catch(() => setActivity([]));
    }
    if (tab === 'audit') {
      void orgApi.getAudit(id).then(setAudit).catch(() => setAudit([]));
    }
    if (tab === 'contacts') {
      void orgApi.listContacts(id).then(setContacts).catch(() => setContacts([]));
    }
    if (tab === 'addresses') {
      void orgApi.listAddresses(id).then(setAddresses).catch(() => setAddresses([]));
    }
  }, [id, tab]);

  async function lifecycle(action: 'suspend' | 'activate' | 'archive' | 'restore') {
    setError('');
    try {
      const fn = { suspend: orgApi.suspend, activate: orgApi.activate, archive: orgApi.archive, restore: orgApi.restore }[action];
      setOrg(await fn(id));
      await loadOrg();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
  }

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    setSaving(true);
    setError('');
    try {
      setOrg(
        await orgApi.update(id, {
          profile: org.profile,
          description: org.description,
          tags: org.tags,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveBranding(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    setSaving(true);
    try {
      setOrg(await orgApi.update(id, { branding: org.branding }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveSettings(e: FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      setSettings(await orgApi.updateSettings(id, settings));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function addContact(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const created = await orgApi.addContact(id, newContact);
      setContacts((prev) => [...prev, created]);
      setNewContact({ name: '', email: '', phone: '', role: '', is_primary: false });
      await loadOrg();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  }

  async function addAddress(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const created = await orgApi.addAddress(id, newAddress);
      setAddresses((prev) => [...prev, created]);
      setNewAddress({
        label: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        is_primary: false,
      });
      await loadOrg();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add address');
    } finally {
      setSaving(false);
    }
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'profile', label: 'Profile' },
    { key: 'branding', label: 'Branding' },
    { key: 'settings', label: 'Settings' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'addresses', label: 'Addresses' },
    { key: 'activity', label: 'Activity' },
    { key: 'audit', label: 'Audit' },
  ];

  if (loading) return <p className="p-6 text-slate-600">Loading…</p>;
  if (!org) return <p className="p-6 text-red-600">{error || 'Organization not found'}</p>;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <Link to="/organizations" className="text-sm text-slate-600 hover:underline">
        ← Organizations
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{org.name}</h1>
          <p className="text-sm text-slate-600">
            {org.slug} · <span className="capitalize">{org.status}</span>
          </p>
          <Link to={`/organizations/${id}/dashboard`} className="mt-1 inline-block text-sm text-slate-700 hover:underline">
            Open dashboard
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {org.status === 'active' && (
            <button type="button" onClick={() => void lifecycle('suspend')} className="rounded-lg border border-amber-300 px-3 py-1.5 text-sm text-amber-800">
              Suspend
            </button>
          )}
          {(org.status === 'suspended' || org.status === 'archived') && (
            <button type="button" onClick={() => void lifecycle('activate')} className="rounded-lg border border-emerald-300 px-3 py-1.5 text-sm text-emerald-800">
              Activate
            </button>
          )}
          {org.status === 'active' && (
            <button type="button" onClick={() => void lifecycle('archive')} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">
              Archive
            </button>
          )}
        </div>
      </div>

      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <nav className="mt-6 flex flex-wrap gap-1 border-b border-slate-200" aria-label="Organization sections">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm ${tab === t.key ? 'border-b-2 border-slate-900 font-medium text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === 'overview' && (
          <div className="grid gap-4 md:grid-cols-2">
            <Card title="Identity">
              <dl className="space-y-2 text-sm">
                <Row label="ID" value={org.id} />
                <Row label="Tenant" value={org.tenant_id} />
                <Row label="Created" value={new Date(org.created_at).toLocaleString()} />
                <Row label="Updated" value={new Date(org.updated_at).toLocaleString()} />
              </dl>
            </Card>
            <Card title="Regional">
              <dl className="space-y-2 text-sm">
                <Row label="Timezone" value={org.profile?.timezone ?? '—'} />
                <Row label="Locale" value={org.profile?.locale ?? '—'} />
                <Row label="Currency" value={org.profile?.currency ?? '—'} />
              </dl>
            </Card>
            {org.tags && org.tags.length > 0 && (
              <Card title="Tags">
                <div className="flex flex-wrap gap-2">
                  {org.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {tab === 'profile' && (
          <form onSubmit={saveProfile} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field label="Description" value={org.description ?? ''} onChange={(v) => setOrg({ ...org, description: v })} />
            <Field label="Legal name" value={org.profile?.legal_name ?? ''} onChange={(v) => setOrg({ ...org, profile: { ...org.profile, legal_name: v } })} />
            <Field label="Registration number" value={org.profile?.registration_number ?? ''} onChange={(v) => setOrg({ ...org, profile: { ...org.profile, registration_number: v } })} />
            <Field label="GST number" value={org.profile?.gst_number ?? ''} onChange={(v) => setOrg({ ...org, profile: { ...org.profile, gst_number: v } })} />
            <Field label="Website" value={org.profile?.website ?? ''} onChange={(v) => setOrg({ ...org, profile: { ...org.profile, website: v } })} />
            <Field label="Industry" value={org.profile?.industry ?? ''} onChange={(v) => setOrg({ ...org, profile: { ...org.profile, industry: v } })} />
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-60">
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </form>
        )}

        {tab === 'branding' && (
          <form onSubmit={saveBranding} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field label="Display name" value={org.branding?.display_name ?? ''} onChange={(v) => setOrg({ ...org, branding: { ...org.branding, display_name: v } })} />
            <Field label="Logo URL" value={org.branding?.logo_url ?? ''} onChange={(v) => setOrg({ ...org, branding: { ...org.branding, logo_url: v } })} />
            <Field label="Primary color" value={org.branding?.primary_color ?? ''} onChange={(v) => setOrg({ ...org, branding: { ...org.branding, primary_color: v } })} />
            <Field label="Accent color" value={org.branding?.accent_color ?? ''} onChange={(v) => setOrg({ ...org, branding: { ...org.branding, accent_color: v } })} />
            <Field label="Theme" value={org.branding?.theme ?? ''} onChange={(v) => setOrg({ ...org, branding: { ...org.branding, theme: v } })} />
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-60">
              {saving ? 'Saving…' : 'Save branding'}
            </button>
          </form>
        )}

        {tab === 'settings' && settings && (
          <form onSubmit={saveSettings} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field
              label="Theme setting"
              value={settings.settings?.theme ?? ''}
              onChange={(v) => setSettings({ ...settings, settings: { ...settings.settings, theme: v } })}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settings.preferences?.notifications_enabled ?? false}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    preferences: { ...settings.preferences, notifications_enabled: e.target.checked },
                  })
                }
              />
              Notifications enabled
            </label>
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-60">
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </form>
        )}

        {tab === 'contacts' && (
          <div className="space-y-6">
            <Card title="Contacts">
              {contacts.length > 0 ? (
                <ul className="space-y-3">
                  {contacts.map((c) => (
                    <li key={c.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                      <p className="font-medium">{c.name}</p>
                      <p className="text-slate-600">{c.email || c.phone || '—'}</p>
                      {c.role && <p className="text-slate-500">{c.role}</p>}
                      {c.is_primary && <span className="text-xs text-emerald-700">Primary</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-600">No contacts yet.</p>
              )}
            </Card>
            <form onSubmit={addContact} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="font-medium text-slate-900">Add contact</h3>
              <Field label="Name" value={newContact.name} onChange={(v) => setNewContact({ ...newContact, name: v })} />
              <Field label="Email" value={newContact.email} onChange={(v) => setNewContact({ ...newContact, email: v })} />
              <Field label="Phone" value={newContact.phone} onChange={(v) => setNewContact({ ...newContact, phone: v })} />
              <Field label="Role" value={newContact.role} onChange={(v) => setNewContact({ ...newContact, role: v })} />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newContact.is_primary}
                  onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                />
                Primary contact
              </label>
              <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-60">
                {saving ? 'Adding…' : 'Add contact'}
              </button>
            </form>
          </div>
        )}

        {tab === 'addresses' && (
          <div className="space-y-6">
            <Card title="Addresses">
              {addresses.length > 0 ? (
                <ul className="space-y-3">
                  {addresses.map((a) => (
                    <li key={a.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                      <p className="font-medium">{a.label || 'Address'}</p>
                      <p>{a.line1}</p>
                      {a.line2 && <p>{a.line2}</p>}
                      <p>
                        {a.city}, {a.state} {a.postal_code}
                      </p>
                      <p>{a.country}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-600">No addresses yet.</p>
              )}
            </Card>
            <form onSubmit={addAddress} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="font-medium text-slate-900">Add address</h3>
              <Field label="Label" value={newAddress.label} onChange={(v) => setNewAddress({ ...newAddress, label: v })} />
              <Field label="Line 1" value={newAddress.line1} onChange={(v) => setNewAddress({ ...newAddress, line1: v })} />
              <Field label="Line 2" value={newAddress.line2} onChange={(v) => setNewAddress({ ...newAddress, line2: v })} />
              <Field label="City" value={newAddress.city} onChange={(v) => setNewAddress({ ...newAddress, city: v })} />
              <Field label="State" value={newAddress.state} onChange={(v) => setNewAddress({ ...newAddress, state: v })} />
              <Field
                label="Postal code"
                value={newAddress.postal_code}
                onChange={(v) => setNewAddress({ ...newAddress, postal_code: v })}
              />
              <Field label="Country" value={newAddress.country} onChange={(v) => setNewAddress({ ...newAddress, country: v })} />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newAddress.is_primary}
                  onChange={(e) => setNewAddress({ ...newAddress, is_primary: e.target.checked })}
                />
                Primary address
              </label>
              <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-60">
                {saving ? 'Adding…' : 'Add address'}
              </button>
            </form>
          </div>
        )}

        {tab === 'activity' && (
          <Card title="Activity timeline">
            {activity.length === 0 ? (
              <p className="text-sm text-slate-600">No activity recorded.</p>
            ) : (
              <ul className="space-y-3">
                {activity.map((a) => (
                  <li key={a.id} className="border-l-2 border-slate-200 pl-4 text-sm">
                    <p className="font-medium text-slate-900">{a.summary}</p>
                    <p className="text-xs text-slate-500">
                      {a.action} · {new Date(a.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {tab === 'audit' && (
          <Card title="Audit log">
            {audit.length === 0 ? (
              <p className="text-sm text-slate-600">No audit entries.</p>
            ) : (
              <ul className="space-y-3">
                {audit.map((a) => (
                  <li key={a.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                    <p className="font-medium">{a.action}</p>
                    <p className="text-slate-600">
                      {a.outcome} · {new Date(a.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 font-medium text-slate-900">{title}</h2>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-900">{value}</dd>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
    </label>
  );
}
